import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Evaluation } from '../types';

interface EvaluationsState {
  allEvaluations: Evaluation[];
  myEvaluations: Evaluation[];
  currentEvaluation: Evaluation | null;
  isLoading: boolean;
  lastFetchedAll: number | null;
  lastFetchedMy: number | null;
  cacheExpiry: number; 
}

const initialState: EvaluationsState = {
  allEvaluations: [],
  myEvaluations: [],
  currentEvaluation: null,
  isLoading: false,
  lastFetchedAll: null,
  lastFetchedMy: null,
  cacheExpiry: 5 * 60 * 1000, 
};

const evaluationsSlice = createSlice({
  name: 'evaluations',
  initialState,
  reducers: {
    setAllEvaluations: (state, action: PayloadAction<Evaluation[]>) => {
      state.allEvaluations = action.payload;
      state.lastFetchedAll = Date.now();
      state.isLoading = false;
    },
    setMyEvaluations: (state, action: PayloadAction<Evaluation[]>) => {
      state.myEvaluations = action.payload;
      state.lastFetchedMy = Date.now();
      state.isLoading = false;
    },
    setCurrentEvaluation: (state, action: PayloadAction<Evaluation | null>) => {
      state.currentEvaluation = action.payload;
    },
    updateEvaluation: (state, action: PayloadAction<Evaluation>) => {
      const allIndex = state.allEvaluations.findIndex(e => e._id === action.payload._id);
      if (allIndex !== -1) {
        state.allEvaluations[allIndex] = action.payload;
      }

      const myIndex = state.myEvaluations.findIndex(e => e._id === action.payload._id);
      if (myIndex !== -1) {
        state.myEvaluations[myIndex] = action.payload;
      }

      if (state.currentEvaluation?._id === action.payload._id) {
        state.currentEvaluation = action.payload;
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    invalidateAllCache: (state) => {
      state.lastFetchedAll = null;
    },
    invalidateMyCache: (state) => {
      state.lastFetchedMy = null;
    },
    clearEvaluations: (state) => {
      state.allEvaluations = [];
      state.myEvaluations = [];
      state.currentEvaluation = null;
      state.lastFetchedAll = null;
      state.lastFetchedMy = null;
    },
  },
});

export const {
  setAllEvaluations,
  setMyEvaluations,
  setCurrentEvaluation,
  updateEvaluation,
  setLoading,
  invalidateAllCache,
  invalidateMyCache,
  clearEvaluations,
} = evaluationsSlice.actions;

export const selectAllEvaluations = (state: { evaluations: EvaluationsState }) => state.evaluations.allEvaluations;
export const selectMyEvaluations = (state: { evaluations: EvaluationsState }) => state.evaluations.myEvaluations;
export const selectCurrentEvaluation = (state: { evaluations: EvaluationsState }) => state.evaluations.currentEvaluation;
export const selectIsLoading = (state: { evaluations: EvaluationsState }) => state.evaluations.isLoading;

export const selectShouldFetchAll = (state: { evaluations: EvaluationsState }) => {
  const { lastFetchedAll, cacheExpiry } = state.evaluations;
  if (!lastFetchedAll) return true;
  return Date.now() - lastFetchedAll > cacheExpiry;
};

export const selectShouldFetchMy = (state: { evaluations: EvaluationsState }) => {
  const { lastFetchedMy, cacheExpiry } = state.evaluations;
  if (!lastFetchedMy) return true;
  return Date.now() - lastFetchedMy > cacheExpiry;
};

export default evaluationsSlice.reducer;
